/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { LogService, Logger } from '@epg/angular-base-lib';
import {
  faCheck,
  faExclamation,
  faPlus,
  faSearch,
  faUndo,
  faUser,
  faUserCircle,
  faUserSlash,
  faUsers,
  faUsersSlash
} from '@fortawesome/pro-regular-svg-icons';
import { lastValueFrom, Subscription, timer } from 'rxjs';
import { auditTime, tap } from 'rxjs/operators';
import { TaskAssignmentType } from '../../../../enums/task-assignment-type.enum';
import { TaskStatus } from '../../../../enums/task-status.enum';
import { EmployeeModel } from '../../../../models/employee.model';
import { EmployeeGroupModel } from '../../../../models/employee-group.model';
import { GroupedChipsGroup } from '../../../shared/grouped-chips-autocomplete/grouped-chips-autocomplete.component';
import { SearchModel } from '../../../../models/search.model';
import { TaskModel } from '../../../../models/task.model';
import { WorkflowVersionModel } from '../../../../models/workflow-version.model';
import { WorkflowModel } from '../../../../models/workflow.model';
import { EmployeeService } from '../../../../services/employee.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { TaskService } from '../../../../services/task.service';
import { WorkflowService } from '../../../../services/workflow.service';
import { ScheduledTaskService } from '../../../../services/scheduled-task.service';
import { ScheduledTaskModel } from '../../../../models/scheduled-task.model';
import {
  CustomDateFormatService,
  DateFormat
} from '../../../../services/custom-date-format/custom-date-format.service';
import {
  FilterAdapter,
  FilterPersistenceService
} from '../../../../services/filter-persistence.service';
import { QueryParamGuardService } from '../../../../services/query-param-guard.service';
import { TenantMappingService } from '../../../../services/tenant-mapping.service';
import {
  DateFilterValue,
  DateRangePickerComponent,
  LayoutDirection,
  TaskDateFilterPresets
} from '../../../shared/date-range-picker/date-range-picker.component';

export interface TaskFilter {
  searchText: string;
  dateFilterValue: DateFilterValue;
  status: string[];
  assignmentStatus: string[];
  workflowName: string;
  workflowVersions: string[];
  employees: string[];
  employeeGroups: string[];
  includeGroupMembers: boolean;
  refType?: string;
  refValue: string;
}

export class TaskFilterAdapterImpl implements FilterAdapter<TaskFilter> {
  toParams(filter: TaskFilter): Record<string, string> {
    const params: Record<string, string> = {};
    const defaults = this.getDefaults();

    if (filter.searchText && filter.searchText !== defaults.searchText)
      params['searchText'] = filter.searchText;

    if (filter.dateFilterValue.preset === TaskDateFilterPresets.custom) {
      params['date-filter'] = DateRangePickerComponent.formatDateFilterParam(
        filter.dateFilterValue,
        true
      );
    } else if (filter.dateFilterValue.preset !== defaults.dateFilterValue.preset) {
      params['date-filter-preset'] = DateRangePickerComponent.formatPresetParam(
        filter.dateFilterValue.preset
      );
    }

    if (filter.status.length > 0) params['status'] = filter.status.join(',');
    if (filter.assignmentStatus.length > 0)
      params['assignmentStatus'] = filter.assignmentStatus.join(',');
    if (filter.workflowName) params['workflow'] = filter.workflowName;
    if (filter.workflowVersions.length > 0) params['versions'] = filter.workflowVersions.join(',');
    if (filter.employees.length > 0) params['employees'] = filter.employees.join(',');
    if (filter.employeeGroups.length > 0) params['employeeGroups'] = filter.employeeGroups.join(',');
    if (filter.includeGroupMembers !== defaults.includeGroupMembers)
      params['includeGroupMembers'] = String(filter.includeGroupMembers);
    if (filter.refType) params['refType'] = filter.refType;
    if (filter.refValue) params['refValue'] = filter.refValue;
    return params;
  }

  fromParams(params: Record<string, string>): Partial<TaskFilter> {
    const partial: Partial<TaskFilter> = {};
    if (params['searchText']) partial.searchText = params['searchText'];

    if (params['date-filter']) {
      const parsed = DateRangePickerComponent.parseDateFilterParam(params['date-filter'], true);
      if (parsed) {
        partial.dateFilterValue = parsed;
      }
    } else if (params['date-filter-preset']) {
      const preset = DateRangePickerComponent.parsePresetParam(params['date-filter-preset']);
      if (preset !== null) {
        const dates = DateRangePickerComponent.computeDatesForPreset(preset);
        if (dates) {
          partial.dateFilterValue = {
            startDate: dates.startDate,
            endDate: dates.endDate,
            preset
          };
        }
      }
    }

    if (params['status']) partial.status = params['status'].split(',');
    if (params['assignmentStatus'])
      partial.assignmentStatus = params['assignmentStatus'].split(',');
    if (params['workflow']) partial.workflowName = params['workflow'];
    if (params['versions']) partial.workflowVersions = params['versions'].split(',');
    if (params['employees']) partial.employees = params['employees'].split(',');
    if (params['employeeGroups']) partial.employeeGroups = params['employeeGroups'].split(',');
    if (params['includeGroupMembers'])
      partial.includeGroupMembers = params['includeGroupMembers'] === 'true';
    if (params['refType']) partial.refType = params['refType'];
    if (params['refValue']) partial.refValue = params['refValue'];
    return partial;
  }

  getDefaults(): TaskFilter {
    const defaultDates = DateRangePickerComponent.computeDatesForPreset(
      TaskDateFilterPresets.lastSevenDays
    )!;
    return {
      searchText: '',
      dateFilterValue: {
        startDate: defaultDates.startDate,
        endDate: defaultDates.endDate,
        preset: TaskDateFilterPresets.lastSevenDays
      },
      status: [],
      assignmentStatus: [],
      employees: [],
      employeeGroups: [],
      includeGroupMembers: false,
      workflowName: '',
      workflowVersions: [],
      refValue: '',
      refType: undefined
    };
  }
}

const taskOperationRoute = 'home/tasks/activities';
const STATUS_FILTER_MAP: Record<string, string> = {
  new: TaskStatus[TaskStatus.NEW],
  'in-progress': TaskStatus[TaskStatus.IN_PROGRESS],
  finished: TaskStatus[TaskStatus.CLOSED]
};

@Component({
  selector: 'app-task',
  templateUrl: './task.component.html',
  styleUrls: ['./task.component.scss'],
  standalone: false
})
export class TaskComponent implements OnInit, OnDestroy {
  taskChangedSubscription: Subscription = new Subscription();
  taskDeletedSubscription: Subscription = new Subscription();
  workflowChangedSubscription: Subscription = new Subscription();
  employeesChangedSubscription: Subscription = new Subscription();
  employeeGroupsChangedSubscription: Subscription = new Subscription();
  scheduledTimer: Subscription = new Subscription();
  taskRouteChangeSub: Subscription = new Subscription();
  newTaskCreated: Subscription = new Subscription();
  routerQueryParamsSubscription: Subscription = new Subscription();
  routerEventsSubscription: Subscription = new Subscription();
  viewPortScrollSubscription: Subscription = new Subscription();
  private toggleLabelResizeObserver?: ResizeObserver;
  private paramGuardSub: Subscription = new Subscription();
  faPlus = faPlus;
  faSearch = faSearch;
  faExclamation = faExclamation;
  faUsers = faUsers;
  faUserCircle = faUserCircle;
  faUser = faUser;
  faCheck = faCheck;
  faUndo = faUndo;
  faUserSlash = faUserSlash;
  faUsersSlash = faUsersSlash;
  filterTitle = $localize`:title for task filter panel@@taskFilterPanelHeader:Filter Tasks`;

  showVersionDropdown = false;
  enableTaskAddButton = true;
  showEmptyReferenceValues = true;

  TaskStatus = TaskStatus;
  TaskAssignmentType = TaskAssignmentType;
  readonly LayoutDirection = LayoutDirection;

  newTask?: TaskModel;
  tasks: TaskModel[] = [];
  displayedTasks: TaskModel[] = [];
  workflows: WorkflowModel[] = [];
  employees: EmployeeModel[] = [];
  employeeGroups: EmployeeGroupModel[] = [];
  employeeFilterGroups: GroupedChipsGroup[] = [];
  employeeFilterValue: Record<string, string[]> = {};
  workflowFilterGroups: GroupedChipsGroup[] = [];
  workflowFilterValue: Record<string, string[]> = {};
  versionFilterGroups: GroupedChipsGroup[] = [];
  versionFilterValue: Record<string, string[]> = {};
  // selectedWorkflows: string[] = [];
  workflowVersions: WorkflowVersionModel[] = [];
  versionCreatedAtMap: Record<string, string> = {};
  filteredTasks: TaskModel[] = [];
  statusOptions: string[] = ['new', 'in-progress', 'finished'];
  assignmentOptions: string[] = ['claimed', 'unclaimed'];
  references: string[] = [];
  filterOpen = true;
  apiBody = {
    name: '',
    include_deleted: false
  };
  workflow: any = null;

  private readonly initialDefaultDates = DateRangePickerComponent.computeDatesForPreset(
    TaskDateFilterPresets.lastSevenDays
  )!;

  filter: TaskFilter = {
    searchText: '',
    dateFilterValue: {
      startDate: this.initialDefaultDates.startDate,
      endDate: this.initialDefaultDates.endDate,
      preset: TaskDateFilterPresets.lastSevenDays
    },
    status: [],
    assignmentStatus: [],
    employees: [],
    employeeGroups: [],
    includeGroupMembers: false,
    workflowName: '',
    workflowVersions: [],
    refValue: '',
    refType: undefined
  };

  workflowSearchBody: SearchModel = {
    search_criteria: {},
    limit: 1000, // Increased limit to load all workflows
    skip: 0,
    sort: 'name -version',
    select: '_id name input version initialState executableFromMobile references'
  };

  private log: Logger;

  @ViewChild(CdkVirtualScrollViewport)
  viewPort!: CdkVirtualScrollViewport;

  limit = 20;
  select =
    '_id task_id name workflow_id status assigned_to_employee_or_employeegroup assigned_to claimed_by scheduled_at scheduled_by';
  fetchedAllTasks = false;

  filterInputFieldRecentlyChanged = false;

  activeSearchQuery?: Promise<{ headers: object; body: TaskModel[] }> = undefined;
  tasksResetRequested: boolean = false;
  nextTasksRequested: boolean = false;
  currentTenant: string | undefined;
  currentUser: string | undefined;
  private isFilterInitializationComplete: boolean = false;

  private taskFilterAdapter: FilterAdapter<TaskFilter>;

  get isFilterDefault(): boolean {
    const defaults = this.taskFilterAdapter.getDefaults();
    return (
      this.filter.searchText === defaults.searchText &&
      this.filter.dateFilterValue.preset === defaults.dateFilterValue.preset &&
      this.filter.status.length === 0 &&
      this.filter.assignmentStatus.length === 0 &&
      this.filter.workflowName.length === 0 &&
      this.filter.workflowVersions.length === 0 &&
      this.filter.employees.length === 0 &&
      this.filter.employeeGroups.length === 0 &&
      this.filter.includeGroupMembers === defaults.includeGroupMembers &&
      !this.filter.refType &&
      !this.filter.refValue &&
      this.showEmptyReferenceValues
    );
  }

  get dateFilterValue(): DateFilterValue {
    return this.filter.dateFilterValue;
  }

  onDateFilterChange(filter: DateFilterValue): void {
    if (
      this.filter.dateFilterValue.startDate.getTime() === filter.startDate.getTime() &&
      this.filter.dateFilterValue.endDate.getTime() === filter.endDate.getTime() &&
      this.filter.dateFilterValue.preset === filter.preset
    ) {
      return;
    }

    this.filter.dateFilterValue = filter;
    this.searchTask();
  }

  constructor(
    private route: ActivatedRoute,
    private taskService: TaskService,
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private logService: LogService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private snackbarService: SnackbarService,
    private scheduledTaskService: ScheduledTaskService,
    public customDateFormatService: CustomDateFormatService,
    private tenantMapper: TenantMappingService,
    private filterPersistenceService: FilterPersistenceService,
    private queryParamGuard: QueryParamGuardService
  ) {
    this.log = logService.getLogger(this);
    this.taskFilterAdapter = new TaskFilterAdapterImpl();
  }

  async ngOnInit(): Promise<void> {
    // Capture current user (if available via route data or injected later)
    this.currentUser =
      this.route.snapshot.data?.user?.name ?? this.route.snapshot.data?.user ?? undefined;

    // Restore persisted filter state (URL > session > local > defaults)
    this.filter = this.filterPersistenceService.initialize(
      'filter-tasks',
      this.taskFilterAdapter,
      this.route
    );

    this.paramGuardSub = this.queryParamGuard.protect(this.route, this.router, () =>
      this.taskFilterAdapter.toParams(this.filter)
    );

    this.routerQueryParamsSubscription = this.route.queryParams.subscribe(async (params) => {
      if (!this.isFilterInitializationComplete) {
        return;
      }

      let urlTenant = params.tenant ? params.tenant : '';

      if (urlTenant != '') this.currentTenant = await this.tenantMapper.getActiveTenantName();

      if (
        urlTenant != '' &&
        this.currentTenant &&
        this.currentTenant.toLowerCase() != urlTenant.toLowerCase()
      ) {
        return;
      }

      const currentParams = this.taskFilterAdapter.toParams(this.filter);
      const hasChanged = Object.keys(params).some((key) => {
        if (key === 'tenant') {
          return false;
        }
        const value = Array.isArray(params[key]) ? params[key].join(',') : params[key];
        return value != null && value !== '' && value !== currentParams[key];
      });
      if (!hasChanged) {
        return;
      }

      let refType = params.refType ? params.refType : '';
      let refValue = params.refValue ? params.refValue : '';
      if (Array.isArray(refType)) {
        refType = refType[0];
      }
      if (Array.isArray(refValue)) {
        refValue = refValue[0];
      }
      if (refValue != '') {
        this.filter.refValue = refValue;
      }
      if (refType != '') {
        if (this.references.length === 0) {
          this.workflows = await this.workflowService.searchWorkflow(this.workflowSearchBody);
          this.references = this.extractReferences(this.workflows);
        }
        if (this.references.includes(refType)) {
          this.filter.refType = refType;
        } else {
          this.showInvalidFilterWarning();
        }
      }

      this.searchTask();
    });

    this.newTaskCreated = this.taskService.newTaskCreated.subscribe(async (newTask) => {
      this.handleNewTask(newTask);
    });
    this.taskChangedSubscription = this.taskService.taskChanged.subscribe((updatedTask) => {
      var index = this.tasks.findIndex((task) => task.taskId === updatedTask.taskId);
      if (index !== -1) {
        let tmp = this.tasks.slice();
        tmp.splice(index, 1);
        tmp.splice(index, 0, updatedTask);
        this.tasks = tmp;
        this.showScheduledTasks();
      }
    });

    this.taskDeletedSubscription = this.taskService.taskDeleted.subscribe((deletedTask) => {
      let index = this.tasks.findIndex((task) => task.taskId === deletedTask.taskId);

      if (index > -1) {
        let tmp = this.tasks.slice();
        tmp.splice(index, 1);
        this.tasks = tmp;
        this.showScheduledTasks();

        if (index > 0) {
          index -= 1;
        }

        if (this.tasks.length > index) {
          this.router.navigate([this.tasks[index].taskId], { relativeTo: this.route });
        } else {
          this.router.navigateByUrl(taskOperationRoute);
        }
      }
    });

    this.workflowChangedSubscription = this.workflowService.workflowsChanged.subscribe(
      (newWorkflows) => {
        const selectedWF = this.filter.workflowName;
        this.workflows = newWorkflows;
        this.filter.workflowName = this.workflows.find((wf) => wf.name === selectedWF)?.name ?? '';
        this.references = this.extractReferences(this.workflows);
        this.initializeWorkflowFilterGroups();
      }
    );

    // Load workflows and initialize filter groups
    this.workflows = await this.workflowService.searchWorkflow(this.workflowSearchBody);
    this.references = this.extractReferences(this.workflows);
    this.initializeWorkflowFilterGroups();
    await this.initializeVersionFilterOnLoad();
    this.employeesChangedSubscription = this.employeeService.employeesChanged.subscribe(
      (newEmployees) => {
        this.employees = newEmployees;
        this.initializeEmployeeFilterGroups();
      }
    );
    this.employeeGroupsChangedSubscription = this.employeeService.employeeGroupsChanged.subscribe(
      (newEmployeeGroups) => {
        this.employeeGroups = newEmployeeGroups;
        this.initializeEmployeeFilterGroups();
      }
    );
    this.employees = await this.employeeService.getEmployees();
    this.employeeGroups = await this.employeeService.getEmployeeGroups();
    this.initializeEmployeeFilterGroups();
    this.applyInitialReferenceDateFilterOverride();
    const restoredFilterChanged = this.validateRestoredFilter();
    if (restoredFilterChanged) {
      this.initializeWorkflowFilterGroups();
      this.initializeEmployeeFilterGroups();
      await this.initializeVersionFilterOnLoad();
    }
    this.isFilterInitializationComplete = true;
    this.searchTask();

    this.routerEventsSubscription = this.router.events.subscribe((val) => {
      this.enableAddButton();
    });
    this.enableAddButton();
    const source = timer(0, 60000);
    this.scheduledTimer = source.subscribe(() => this.showScheduledTasks());
  }

  async handleNewTask(newTask: TaskModel): Promise<void> {
    this.newTask = newTask;
    this.tasks = await this.searchTask();
    this.showScheduledTasks();
    const task = this.tasks.find((T) => T.taskId === this.newTask?.taskId);
    let newscheduledTask!: ScheduledTaskModel;

    if (this.newTask.scheduled_by) {
      const data$ = this.scheduledTaskService.getScheduledTask(this.newTask.scheduled_by);
      newscheduledTask = await lastValueFrom(data$);
    }

    if (
      (((task && task.scheduled_at) || (!task && newTask && newTask.scheduled_at)) &&
        Date.parse(newTask.scheduled_at!) > Date.now()) ||
      newscheduledTask?.recurring
    ) {
      let snackbarActionSubscription: Subscription;
      const snackbarRef = this.snackbarService.showSnackBar(
        $localize`:@@taskSuccessfullyScheduledMessage:Task "${this.newTask.name}" has been scheduled successfully`,
        $localize`:Open a page or dialog@@open:Open`
      );
      snackbarActionSubscription = snackbarRef.onAction().subscribe(() => {
        this.router.navigateByUrl(`home/tasks/scheduled/${this.newTask?.scheduled_by}`);
        snackbarActionSubscription.unsubscribe();
      });

      this.router.navigateByUrl(taskOperationRoute);
    } else if (task) {
      this.snackbarService.showSnackBar(
        $localize`:task created message@@taskSuccessfullyCreatedMessage:Task "${this.newTask.name}" has been created successfully`
      );
      this.router.navigateByUrl(taskOperationRoute + '/' + task.taskId);

      const displayedTaskIndex = this.displayedTasks.findIndex(
        (displayedTask) => displayedTask.taskId === newTask.taskId
      );
      if (displayedTaskIndex > -1) {
        this.viewPort.scrollToIndex(displayedTaskIndex);
      }
    } else if (!task && newTask) {
      let snackbarActionSubscription: Subscription;
      const snackbarRef = this.snackbarService.showSnackBar(
        $localize`Task "${this.newTask.name}" has been created successfully. Display it by resetting the filter.`,
        $localize`:@@:Reset filter`
      );
      snackbarActionSubscription = snackbarRef.onAction().subscribe(() => {
        this.resetFilter();
        this.searchTask();
        this.router.navigateByUrl(taskOperationRoute + '/' + this.newTask?.taskId);
        snackbarActionSubscription.unsubscribe();
      });
      this.router.navigateByUrl(taskOperationRoute);
    }
  }

  enableAddButton() {
    if (this.router.url === `/${taskOperationRoute}/task-manager`) {
      this.enableTaskAddButton = false;
    } else {
      this.enableTaskAddButton = true;
    }
  }
  ngOnDestroy(): void {
    this.taskChangedSubscription.unsubscribe();
    this.taskDeletedSubscription.unsubscribe();
    this.workflowChangedSubscription.unsubscribe();
    this.employeesChangedSubscription.unsubscribe();
    this.employeeGroupsChangedSubscription.unsubscribe();
    this.newTaskCreated.unsubscribe();
    this.taskRouteChangeSub.unsubscribe();
    this.scheduledTimer.unsubscribe();
    this.routerQueryParamsSubscription.unsubscribe();
    this.routerEventsSubscription.unsubscribe();
    this.viewPortScrollSubscription.unsubscribe();
    this.paramGuardSub.unsubscribe();
    this.toggleLabelResizeObserver?.disconnect();
  }

  extractReferences(workflows: WorkflowModel[]): string[] {
    let referenceList: string[] = [];
    for (let workflow of workflows) {
      if (workflow.references == undefined) {
        continue;
      }
      for (let reference of workflow.references) {
        if (!referenceList.includes(reference)) {
          referenceList.push(reference);
        }
      }
    }
    return referenceList.sort((a, b) => {
      return a.localeCompare(b);
    });
  }

  ngAfterViewInit() {
    this.viewPortScrollSubscription = this.viewPort.scrolledIndexChange
      .pipe(
        auditTime(300),
        tap((currIndex: number) => {
          if (currIndex > 0) {
            // Fetch new tasks,
            // if there's less than two pages of tasks left below the current scroll position.
            const pageLength =
              this.viewPort.getRenderedRange().end - this.viewPort.getRenderedRange().start;
            const dataLength = this.viewPort.getDataLength();
            if (dataLength - currIndex < pageLength * 2 && !this.fetchedAllTasks) {
              this.searchTask(false);
            }
          }
        })
      )
      .subscribe();
    queueMicrotask(() => this.initFilterToggleLabelAutoFit());
    // setTimeout(() => this.infinite.next(this.arr), 300);
  }

  private initFilterToggleLabelAutoFit(): void {
    this.fitFilterToggleLabels();

    if (typeof ResizeObserver === 'undefined') return;

    this.toggleLabelResizeObserver = new ResizeObserver(() => this.fitFilterToggleLabels());
    document
      .querySelectorAll('.filter-toggle mat-button-toggle-group')
      .forEach((group) => this.toggleLabelResizeObserver?.observe(group));
  }

  private fitFilterToggleLabels(): void {
    document
      .querySelectorAll<HTMLElement>('.filter-toggle mat-button-toggle-group')
      .forEach((group) => this.fitToggleGroupLabels(group));
  }

  private fitToggleGroupLabels(group: HTMLElement): void {
    const maxFontSize = 14;
    const minFontSize = 10;
    const fontStep = 0.5;

    const labels = Array.from(
      group.querySelectorAll<HTMLElement>('.mat-button-toggle-label-content')
    );
    if (!labels.length) return;

    // Reset to defaults
    this.applyLabelStyles(labels, maxFontSize, false);

    // Step 1: shrink font until all labels fit on a single line
    for (let size = maxFontSize; size >= minFontSize; size -= fontStep) {
      this.applyLabelStyles(labels, size, false);
      if (!labels.some((l) => l.scrollWidth > l.clientWidth + 1)) {
        this.updateTooltips(labels);
        return;
      }
    }

    // Step 2: wrap overflowing labels that contain whitespace
    labels.forEach((label) => {
      if (label.scrollWidth > label.clientWidth + 1 && /\s/.test(label.textContent?.trim() ?? '')) {
        this.applyLabelStyles([label], minFontSize, true);
      }
    });

    this.updateTooltips(labels);
  }

  private applyLabelStyles(labels: HTMLElement[], fontSize: number, multiline: boolean): void {
    const lineHeight = fontSize + 6;
    labels.forEach((label) => {
      label.style.setProperty('--toggle-label-font-size', `${fontSize}px`);
      label.style.setProperty('--toggle-label-line-height', `${lineHeight}px`);
      label.classList.toggle('toggle-label--multi-line', multiline);
      label.closest('mat-button-toggle')?.classList.toggle('toggle-item--multi-line', multiline);
    });
  }

  private updateTooltips(labels: HTMLElement[]): void {
    labels.forEach((label) => {
      const isMultiline = label.classList.contains('toggle-label--multi-line');
      const overflows = isMultiline
        ? label.scrollHeight > label.clientHeight + 1 || label.scrollWidth > label.clientWidth + 1
        : label.scrollWidth > label.clientWidth + 1;
      label.title = overflows ? (label.textContent?.trim() ?? '') : '';
    });
  }

  async fillVersionDropdown(workflow: { _id: string; name: string }) {
    this.clearWorkflowVersionFilter();
    // shows the version filter and the clear button
    this.showVersionDropdown = true;
    this.workflowVersions = await this.workflowService.getWorkflowVersions(workflow.name);
    this.searchTask();
  }

  //function to clear the workflow form field
  clearWorkflowFilter() {
    this.filter.workflowName = '';
    this.filter.workflowVersions = [];
    //Don´t show the clear button and the version dropdown
    this.showVersionDropdown = false;
    this.searchTask();
  }

  clearWorkflowVersionFilter() {
    this.filter.workflowVersions = [];
  }

  async searchTask(resetTaskList: boolean = true): Promise<TaskModel[]> {
    // Persist current filter before executing search
    this.persistFilter();

    if (this.activeSearchQuery != null && this.activeSearchQuery != undefined) {
      if (resetTaskList) {
        this.tasksResetRequested = true;
      } else {
        this.nextTasksRequested = true;
      }
    } else {
      if (resetTaskList) {
        this.tasks = [];
        this.displayedTasks = [];
      }

      this.activeSearchQuery = this.taskService.searchTask(
        this.createSearchQuery(this.tasks.length),
        resetTaskList
      );
      this.activeSearchQuery
        .then((response) => {
          let newTasks = response.body;

          // check whether all the tasks have been fetched
          if (newTasks.length < this.limit) {
            this.fetchedAllTasks = true;
          } else {
            this.fetchedAllTasks = false;
          }

          if (resetTaskList) {
            this.tasks = newTasks.slice();
          } else {
            this.tasks = this.tasks.concat(newTasks.slice());
          }

          this.showScheduledTasks();

          this.cdr.detectChanges();

          if (
            this.viewPort.getDataLength() <=
              this.viewPort.getRenderedRange().end - this.viewPort.getRenderedRange().start &&
            !this.fetchedAllTasks
          ) {
            this.nextTasksRequested = true;
          }
        })
        .finally(() => {
          this.activeSearchQuery = undefined;

          if (this.tasksResetRequested) {
            this.tasksResetRequested = false;
            this.nextTasksRequested = false;
            this.searchTask();
          } else if (this.nextTasksRequested) {
            this.tasksResetRequested = false;
            this.nextTasksRequested = false;
            this.searchTask(false);
          }
        });
    }

    return (await this.activeSearchQuery).body;
  }

  resetFilter() {
    this.filter = this.taskFilterAdapter.getDefaults();
    this.showEmptyReferenceValues = true;
    this.employeeFilterValue = {
      employees: [],
      employeeGroups: []
    };
    // Reset to "All workflows" option
    this.workflowFilterValue = { 'workflow-presets': [''] };
    this.workflowVersions = [];
    this.versionCreatedAtMap = {};
    this.versionFilterGroups = [
      {
        key: 'version-presets',
        label: '',
        items: [
          {
            id: 'all',
            name: $localize`:@@all:All`,
            displayName: $localize`:@@allVersions:All versions`
          }
        ]
      }
    ];
    this.versionFilterValue = { 'version-presets': ['all'] };
  }

  private validateRestoredFilter(): boolean {
    const defaults = this.taskFilterAdapter.getDefaults();
    let changed = false;
    let dateWarningShown = false;
    let hasInvalidFilterWarning = false;
    const workflowNames = new Set(this.workflows.map((workflow) => workflow.name));

    const statusValues = this.normalizeFilterValues(this.filter.status);
    const validStatusValues = new Set(this.statusOptions);
    const filteredStatusValues = statusValues.filter((status) => validStatusValues.has(status));
    const invalidStatusValues = statusValues.filter((status) => !validStatusValues.has(status));
    if (invalidStatusValues.length > 0) {
      hasInvalidFilterWarning = true;
    }
    if (!this.areStringArraysEqual(this.filter.status, filteredStatusValues)) {
      this.filter.status = filteredStatusValues;
      changed = true;
    }

    const assignmentValues = this.normalizeFilterValues(this.filter.assignmentStatus);
    const validAssignmentValues = new Set(this.assignmentOptions);
    const filteredAssignmentValues = assignmentValues.filter((status) =>
      validAssignmentValues.has(status)
    );
    const invalidAssignmentValues = assignmentValues.filter(
      (status) => !validAssignmentValues.has(status)
    );
    if (invalidAssignmentValues.length > 0) {
      hasInvalidFilterWarning = true;
    }
    if (!this.areStringArraysEqual(this.filter.assignmentStatus, filteredAssignmentValues)) {
      this.filter.assignmentStatus = filteredAssignmentValues;
      changed = true;
    }

    if (this.filter.workflowName && !workflowNames.has(this.filter.workflowName)) {
      hasInvalidFilterWarning = true;
      this.filter.workflowName = defaults.workflowName;
      changed = true;
    }

    const versionValues = this.normalizeFilterValues(this.filter.workflowVersions);
    let filteredVersionValues = versionValues;
    let invalidVersionValues: string[] = [];
    if (versionValues.includes('all')) {
      filteredVersionValues = [];
    } else if (!this.filter.workflowName) {
      filteredVersionValues = [];
      invalidVersionValues = versionValues;
    } else {
      const validVersionValues = new Set(this.workflowVersions.map((version) => version._id));
      filteredVersionValues = versionValues.filter(
        (version) => version === 'latest' || validVersionValues.has(version)
      );
      invalidVersionValues = versionValues.filter(
        (version) => version !== 'latest' && !validVersionValues.has(version)
      );
    }
    if (invalidVersionValues.length > 0) {
      hasInvalidFilterWarning = true;
    }
    if (!this.areStringArraysEqual(this.filter.workflowVersions, filteredVersionValues)) {
      this.filter.workflowVersions = filteredVersionValues;
      changed = true;
    }

    const employeeValues = this.normalizeFilterValues(this.filter.employees);
    const validEmployeeValues = new Set(this.employees.map((employee) => employee.employeeId));
    const filteredEmployeeValues = employeeValues.filter((employeeId) =>
      validEmployeeValues.has(employeeId)
    );
    const invalidEmployeeValues = employeeValues.filter(
      (employeeId) => !validEmployeeValues.has(employeeId)
    );
    if (invalidEmployeeValues.length > 0) {
      hasInvalidFilterWarning = true;
    }
    if (!this.areStringArraysEqual(this.filter.employees, filteredEmployeeValues)) {
      this.filter.employees = filteredEmployeeValues;
      changed = true;
    }

    const employeeGroupValues = this.normalizeFilterValues(this.filter.employeeGroups);
    const validEmployeeGroupValues = new Set(
      this.employeeGroups.map((employeeGroup) => employeeGroup.employeeGroupId)
    );
    const filteredEmployeeGroupValues = employeeGroupValues.filter((groupId) =>
      validEmployeeGroupValues.has(groupId)
    );
    const invalidEmployeeGroupValues = employeeGroupValues.filter(
      (groupId) => !validEmployeeGroupValues.has(groupId)
    );
    if (invalidEmployeeGroupValues.length > 0) {
      hasInvalidFilterWarning = true;
    }
    if (!this.areStringArraysEqual(this.filter.employeeGroups, filteredEmployeeGroupValues)) {
      this.filter.employeeGroups = filteredEmployeeGroupValues;
      changed = true;
    }

    if (this.filter.employeeGroups.length === 0 && this.filter.includeGroupMembers) {
      this.filter.includeGroupMembers = false;
      changed = true;
    }

    const refType = this.filter.refType?.trim() || undefined;
    if (refType !== this.filter.refType) {
      this.filter.refType = refType;
      changed = true;
    }
    if (this.filter.refType && !this.references.includes(this.filter.refType)) {
      hasInvalidFilterWarning = true;
      this.filter.refType = defaults.refType;
      changed = true;
    }

    const rawDateFilter = this.route.snapshot.queryParamMap.get('date-filter');
    const rawDateFilterPreset = this.route.snapshot.queryParamMap.get('date-filter-preset');
    if (rawDateFilter) {
      if (!DateRangePickerComponent.parseDateFilterParam(rawDateFilter, true)) {
        hasInvalidFilterWarning = true;
        this.filter.dateFilterValue = defaults.dateFilterValue;
        changed = true;
        dateWarningShown = true;
      }
    } else if (rawDateFilterPreset) {
      const parsedPreset = DateRangePickerComponent.parsePresetParam(rawDateFilterPreset);
      if (parsedPreset === null || parsedPreset === TaskDateFilterPresets.custom) {
        hasInvalidFilterWarning = true;
        this.filter.dateFilterValue = defaults.dateFilterValue;
        changed = true;
        dateWarningShown = true;
      }
    }

    const startDate = this.filter.dateFilterValue?.startDate;
    const endDate = this.filter.dateFilterValue?.endDate;
    const hasValidDateRange =
      startDate instanceof Date &&
      endDate instanceof Date &&
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(endDate.getTime()) &&
      startDate.getTime() <= endDate.getTime();
    if (!hasValidDateRange) {
      if (!dateWarningShown) {
        hasInvalidFilterWarning = true;
      }
      this.filter.dateFilterValue = defaults.dateFilterValue;
      changed = true;
    }

    if (hasInvalidFilterWarning) {
      this.showInvalidFilterWarning();
    }

    return changed;
  }

  private showInvalidFilterWarning(): void {
    this.snackbarService.showSnackBarWithTitle(
      $localize`:invalid filter title@@invalidFilterTitle:Invalid search query`,
      $localize`:invalid filter summary message@@invalidFilterSummaryMessage:Some filter values are invalid or no longer available and were removed.`,
      $localize`:@@shared.button.ok:OK`,
      'warn'
    );
  }

  private applyInitialReferenceDateFilterOverride(): void {
    const queryParamMap = this.route.snapshot.queryParamMap;
    if (queryParamMap.has('date-filter') || queryParamMap.has('date-filter-preset')) {
      return;
    }

    const refValue = queryParamMap.get('refValue')?.trim() || '';
    const refType = queryParamMap.get('refType')?.trim() || '';
    if (!refValue && (!refType || !this.references.includes(refType))) {
      return;
    }

    this.filter.dateFilterValue = {
      ...this.filter.dateFilterValue,
      startDate: new Date('2021-01-01'),
      preset: TaskDateFilterPresets.custom
    };
  }

  private normalizeFilterValues(values: string[] = []): string[] {
    return Array.from(
      new Set(
        values
          .filter((value): value is string => typeof value === 'string')
          .map((value) => value.trim())
          .filter((value) => value !== '')
      )
    );
  }

  private areStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((value, index) => value === right[index]);
  }

  private async initializeVersionFilterOnLoad(): Promise<void> {
    // No workflow selected: keep a simple "All" placeholder and empty versions list
    if (!this.filter.workflowName) {
      this.workflowVersions = [];
      this.versionCreatedAtMap = {};
      this.versionFilterGroups = [
        {
          key: 'version-presets',
          label: '',
          items: [
            {
              id: 'all',
              name: $localize`:@@all:All`,
              displayName: $localize`:@@allVersions:All versions`
            }
          ]
        }
      ];
      this.versionFilterValue = { 'version-presets': ['all'] };
      return;
    }

    const workflow = this.workflows.find((wf) => wf.name === this.filter.workflowName);

    // If the stored workflowName no longer exists, fall back to generic "All" state
    if (!workflow) {
      this.workflowVersions = [];
      this.versionCreatedAtMap = {};
      this.versionFilterGroups = [
        {
          key: 'version-presets',
          label: '',
          items: [
            {
              id: 'all',
              name: $localize`:@@all:All`,
              displayName: $localize`:@@allVersions:All versions`
            }
          ]
        }
      ];
      this.versionFilterValue = { 'version-presets': ['all'] };
      return;
    }

    // Load versions for the selected workflow so we can align UI with restored filter
    this.workflowVersions = await this.workflowService.getWorkflowVersions(workflow.name, true);
    this.workflowVersions.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    this.versionCreatedAtMap = {};
    this.workflowVersions.forEach((v) => {
      if (v.createdAt) {
        this.versionCreatedAtMap[v._id] = this.formatVersionDate(v.createdAt);
      }
    });

    this.versionFilterGroups = [
      {
        key: 'version-presets',
        label: '',
        items: [
          {
            id: 'all',
            name: $localize`:@@all:All`,
            displayName: $localize`:@@allVersions:All versions`
          },
          {
            id: 'latest',
            name: $localize`:@@latest:Latest`,
            displayName: $localize`:@@latestVersion:Latest version`
          }
        ]
      },
      {
        key: 'versions',
        label: '',
        items: this.workflowVersions.map((v) => {
          const deletedLabel = $localize`:@@deleted:deleted`;
          return {
            id: v._id,
            name: v.is_deleted ? `${v.version} (${deletedLabel})` : `${v.version}`,
            disabled: v.is_deleted
          };
        })
      }
    ];

    const selected = this.filter.workflowVersions || [];

    // No explicit versions stored -> treat as "All versions"
    if (selected.length === 0) {
      this.versionFilterValue = { 'version-presets': ['all'] };
      return;
    }
    // 'latest' is stored as a literal keyword
    if (selected[0] === 'latest') {
      this.versionFilterValue = { 'version-presets': ['latest'] };
      return;
    }
    // Otherwise, show the first stored version ID as the current selection
    this.versionFilterValue = { versions: [selected[0]] };
  }

  onFilterReset() {
    this.filterPersistenceService.reset('filter-tasks', this.router);
    this.resetFilter();
    this.searchTask();
  }

  persistFilter() {
    this.filterPersistenceService.persist(
      'filter-tasks',
      this.taskFilterAdapter,
      this.filter,
      this.router
    );
  }

  createSearchQuery(skip: number = 0): SearchModel {
    let andBlock = [];

    let referencesElemMatch: { refType?: string; refValue?: object } = {};

    // search
    if (this.filter.searchText.length > 0) {
      //Regex reserved characters escape
      let searchText = this.escapeRegexReservedCharacters(this.filter.searchText);
      andBlock.push({
        $or: [
          { name: { $regex: searchText, $options: 'i' } },
          { description: { $regex: searchText, $options: 'i' } }
        ]
      });
    }

    // status
    if (this.filter.status.length > 0 && this.filter.status.length < this.statusOptions.length) {
      andBlock.push({
        status: {
          $in: this.filter.status
            .filter((s) => s in STATUS_FILTER_MAP)
            .map((s) => STATUS_FILTER_MAP[s])
        }
      });
    }

    // claimed
    if (
      this.filter.assignmentStatus.indexOf('claimed') !== -1 &&
      this.filter.assignmentStatus.indexOf('unclaimed') == -1
    ) {
      andBlock.push({
        $or: [
          { claimed_by: { $not: { $eq: '' } } },
          {
            $and: [
              { assigned_to_employee_or_employeegroup: { $eq: 'EMPLOYEE' } },
              {
                $expr: {
                  $eq: [
                    {
                      $size: { $ifNull: ['$assigned_to', []] }
                    },
                    1
                  ]
                }
              }
            ]
          }
        ]
      });
    }

    // unclaimed
    if (
      this.filter.assignmentStatus.indexOf('unclaimed') !== -1 &&
      this.filter.assignmentStatus.indexOf('claimed') == -1
    ) {
      andBlock.push({
        $and: [
          { claimed_by: { $eq: '' } },
          {
            $or: [
              { assigned_to_employee_or_employeegroup: { $eq: 'EMPLOYEEGROUP' } },
              {
                $and: [
                  { assigned_to_employee_or_employeegroup: { $eq: 'EMPLOYEE' } },
                  {
                    $expr: {
                      $gt: [
                        {
                          $size: { $ifNull: ['$assigned_to', []] }
                        },
                        1
                      ]
                    }
                  }
                ]
              }
            ]
          }
        ]
      });
    }

    // // grouped
    // if (this.currentFilters.indexOf('grouped') !== -1) {
    //   andBlock.push({
    //     $or: [
    //       { assigned_to_employee_or_employeegroup: { $eq: 'EMPLOYEEGROUP' } },
    //       {
    //         $and: [
    //           { assigned_to_employee_or_employeegroup: { $eq: 'EMPLOYEE' } },
    //           {
    //             $expr: {
    //               $gt: [
    //                 {
    //                   $size: '$assigned_to'
    //                 },
    //                 1
    //               ]
    //             }
    //           }
    //         ]
    //       }
    //     ]
    //   });
    // }

    // workflow
    // if (this.filter.workflows.length > 0) {
    //  andBlock.push({ workflow_id: { $in: this.filter.workflows } });
    // }

    // Tasks with a specific workflow version
    if (this.filter.workflowName) {
      if (this.filter.workflowVersions.length > 0) {
        const resolvedVersionIds = this.resolveWorkflowVersionIds(this.filter.workflowVersions);
        andBlock.push({ workflow_id: { $in: resolvedVersionIds } });
      } else {
        andBlock.push({ workflow_id: { $in: this.workflowVersions.map((wf) => wf._id) } });
      }
    }

    // employee
    if (this.filter.employees.length > 0 || this.filter.employeeGroups.length > 0) {
      const combinedIds = this.filter.employees
        .concat(this.filter.employeeGroups)
        .concat(this.getSelectedGroupMemberIds());
      andBlock.push({
        $or: [
          { claimed_by: { $in: combinedIds } },
          { assigned_to: { $in: combinedIds } },
          { executed_by: { $in: combinedIds } }
        ]
      });
    }

    // start-end date range
    const startDate = this.filter.dateFilterValue.startDate.toISOString();
    const endDate = this.filter.dateFilterValue.endDate.toISOString();
    andBlock.push({
      $or: [
        {
          createdAt: {
            $gte: startDate,
            $lte: endDate
          }
        },
        {
          scheduled_at: {
            $gte: startDate,
            $lte: endDate
          }
        }
      ]
    });

    // references
    if (this.filter.refType) {
      referencesElemMatch.refType = this.filter.refType;
    }
    if (this.filter.refValue && this.filter.refValue.length > 0) {
      referencesElemMatch.refValue = {
        $regex: this.escapeRegexReservedCharacters(this.filter.refValue),
        $options: 'i'
      };
    } else if (!this.showEmptyReferenceValues && this.filter.refType) {
      referencesElemMatch.refValue = { $regex: '\\S' };
    }
    if (referencesElemMatch.refType !== undefined || referencesElemMatch.refValue !== undefined) {
      andBlock.push({ references: { $elemMatch: referencesElemMatch } });
    }

    let search_criteria = {
      $and: andBlock
    };

    let searchModel: SearchModel = {
      search_criteria: search_criteria,
      limit: this.limit,
      skip: skip,
      sort: '-updatedAt',
      select: this.select
    };

    return searchModel;
  }
  escapeRegexReservedCharacters(searchText: string): string {
    const normalized = searchText.replace(/\s+/g, ' ').trim();
    const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const flexible = escaped.replace(/ /g, '\\s+');
    return flexible;
  }
  async filterInputFieldChanged() {
    if (this.filterInputFieldRecentlyChanged) return;

    this.filterInputFieldRecentlyChanged = true;
    await new Promise((resolve) => setTimeout(resolve, 300));
    this.filterInputFieldRecentlyChanged = false;

    this.searchTask();
  }
  pageChangedEvents(pageEvent: PageEvent) {}
  scrollOffset(event: any) {}
  trackByIdx(index: number, task: TaskModel) {
    return `${index}-${task.taskId}-${task.isClaimed}`;
  }
  isTaskClaimed(task: TaskModel): boolean {
    if (task.claimedBy != null && task.claimedBy.length > 0) {
      return true;
    } else {
      return false;
    }
  }
  getEmployeeShort(empid: string): string {
    const empName = this.employeeService.getEmployeeNameFromID(empid);
    if (empName == null) {
      return '';
    } else {
      return empName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();
    }
  }
  showScheduledTasks() {
    const currentTime = Date.now();
    this.displayedTasks = this.tasks.filter((task) => {
      if (task.scheduled_at) {
        const taskTime = Date.parse(task.scheduled_at);
        return currentTime - taskTime >= 0;
      } else return true;
    });
  }

  employeeExists(id: string): boolean {
    return this.employeeService.getEmployeeUserNameFromID(id) !== undefined;
  }

  employeeGroupsExist(groupIds: string[]): number {
    let existCount = 0;

    groupIds.forEach((id) => {
      if (this.employeeService.getEmployeeGroupNameFromID(id) !== undefined) {
        existCount++;
      }
    });

    return existCount;
  }

  employeeHasGroups(id: string, groupIds: string[]): boolean {
    let hasGroups = false;
    const employee = this.employeeService.employees.find((emp) => emp.employeeId === id);

    if (employee) {
      for (const empGroup of employee.employeeGroup) {
        if (groupIds.some((group) => group === empGroup)) {
          hasGroups = true;
          break;
        }
      }
    }

    return hasGroups;
  }

  initializeEmployeeFilterGroups(): void {
    this.employeeFilterGroups = [
      {
        key: 'employees',
        label: $localize`:@@employeesLabel:Employees`,
        icon: faUser,
        items: this.employees.map((emp) => ({
          id: emp.employeeId,
          name: emp.userName,
          url: '/home/employees/'
        }))
      },
      {
        key: 'employeeGroups',
        label: $localize`:@@employeeGroupsLabel:Employee Groups`,
        icon: faUsers,
        items: this.employeeGroups.map((group) => ({
          id: group.employeeGroupId,
          name: group.name,
          url: '/home/employee-groups/'
        }))
      }
    ];

    this.employeeFilterValue = {
      employees: this.filter.employees,
      employeeGroups: this.filter.employeeGroups
    };
  }

  onEmployeeFilterChange(value: Record<string, string[]>): void {
    this.filter.employees = value?.employees || [];
    this.filter.employeeGroups = value?.employeeGroups || [];
    if (this.filter.employeeGroups.length === 0) {
      this.filter.includeGroupMembers = false;
    }
    this.searchTask();
  }

  /**
   * Ids of the employees belonging to the selected employee groups.
   * Resolved from the already loaded employee list, so no additional request is needed.
   * Employees selected explicitly are skipped to keep the query small.
   */
  private getSelectedGroupMemberIds(): string[] {
    if (!this.filter.includeGroupMembers || this.filter.employeeGroups.length === 0) {
      return [];
    }

    const selectedGroupIds = new Set(this.filter.employeeGroups);
    const selectedEmployeeIds = new Set(this.filter.employees);

    return this.employees
      .filter(
        (employee) =>
          !selectedEmployeeIds.has(employee.employeeId) &&
          employee.employeeGroup?.some((groupId) => selectedGroupIds.has(groupId))
      )
      .map((employee) => employee.employeeId);
  }

  initializeWorkflowFilterGroups(): void {
    // Group workflows by name and get the latest version for each
    const workflowMap = new Map<string, WorkflowModel>();

    this.workflows.forEach((workflow) => {
      const existing = workflowMap.get(workflow.name);
      if (!existing || workflow.version > existing.version) {
        workflowMap.set(workflow.name, workflow);
      }
    });

    // Create items without version counts initially for fast rendering
    const sortedItems = Array.from(workflowMap.values())
      .map((workflow) => ({
        id: workflow.name,
        name: workflow.name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.workflowFilterGroups = [
      {
        key: 'workflow-presets',
        label: '',
        items: [{ id: '', name: $localize`:@@allWorkflows:All workflows` }]
      },
      {
        key: 'workflows',
        label: '',
        items: sortedItems
      }
    ];

    // Set current workflow selection
    if (this.filter.workflowName) {
      this.workflowFilterValue = {
        workflows: [this.filter.workflowName]
      };
    } else {
      this.workflowFilterValue = { 'workflow-presets': [''] };
    }
  }

  async onWorkflowFilterChange(value: Record<string, string[]>): Promise<void> {
    const workflowPresets = value?.['workflow-presets'] || [];
    const workflowNames = value?.workflows || [];

    // Check if "All workflows" is selected (empty string from presets)
    if (workflowPresets.length > 0 && workflowPresets[0] === '') {
      this.filter.workflowName = '';
      this.workflowVersions = [];
      this.versionCreatedAtMap = {};
      // Reset to default "All" state
      this.versionFilterGroups = [
        {
          key: 'version-presets',
          label: '',
          items: [
            {
              id: 'all',
              name: $localize`:@@all:All`,
              displayName: $localize`:@@allVersions:All versions`
            }
          ]
        }
      ];
      this.versionFilterValue = { 'version-presets': ['all'] };
      this.filter.workflowVersions = [];
    } else if (workflowNames.length > 0) {
      const workflowName = workflowNames[0]; // Single-select mode
      const workflow = this.workflows.find((w) => w.name === workflowName);
      this.filter.workflowName = workflow?.name ?? '';

      if (workflow) {
        await this.loadVersionsForWorkflow(workflow);
      }
    } else {
      this.filter.workflowName = '';
      this.workflowVersions = [];
      this.versionCreatedAtMap = {};
      // Reset to default "All" state
      this.versionFilterGroups = [
        {
          key: 'version-presets',
          label: '',
          items: [
            {
              id: 'all',
              name: $localize`:@@all:All`,
              displayName: $localize`:@@allVersions:All versions`
            }
          ]
        }
      ];
      this.versionFilterValue = { 'version-presets': ['all'] };
      this.filter.workflowVersions = [];
    }

    this.searchTask();
  }

  async loadVersionsForWorkflow(workflow: WorkflowModel): Promise<void> {
    this.workflowVersions = await this.workflowService.getWorkflowVersions(workflow.name, true);
    this.workflowVersions.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    this.versionCreatedAtMap = {};
    this.workflowVersions.forEach((v) => {
      if (v.createdAt) {
        this.versionCreatedAtMap[v._id] = this.formatVersionDate(v.createdAt);
      }
    });

    this.versionFilterGroups = [
      {
        key: 'version-presets',
        label: '',
        items: [
          {
            id: 'all',
            name: $localize`:@@all:All`,
            displayName: $localize`:@@allVersions:All versions`
          },
          {
            id: 'latest',
            name: $localize`:@@latest:Latest`,
            displayName: $localize`:@@latestVersion:Latest version`
          }
        ]
      },
      {
        key: 'versions',
        label: '',
        items: this.workflowVersions.map((v) => {
          const deletedLabel = $localize`:@@deleted:deleted`;
          return {
            id: v._id,
            name: v.is_deleted ? `${v.version} (${deletedLabel})` : `${v.version}`,
            disabled: v.is_deleted
          };
        })
      }
    ];

    // Automatically select "All versions" by default
    this.versionFilterValue = { 'version-presets': ['all'] };
    // Represent "all versions" as an empty filter value
    this.filter.workflowVersions = [];
  }

  onVersionFilterChange(value: Record<string, string[]>): void {
    const presetIds = value?.['version-presets'] || [];
    const versionIds = value?.versions || [];

    if (presetIds.length > 0) {
      const presetId = presetIds[0];
      if (presetId === 'all') {
        this.filter.workflowVersions = [];
      } else if (presetId === 'latest') {
        this.filter.workflowVersions = ['latest'];
      }
    } else if (versionIds.length > 0) {
      this.filter.workflowVersions = [versionIds[0]];
    } else {
      this.filter.workflowVersions = [];
    }

    this.searchTask();
  }

  private resolveWorkflowVersionIds(versionIds: string[]): string[] {
    return versionIds.map((id) => {
      if (id === 'latest') {
        const nonDeleted = this.workflowVersions.filter((v) => !v.is_deleted);
        if (nonDeleted.length > 0) {
          return nonDeleted.reduce((prev, current) =>
            current.version > prev.version ? current : prev
          )._id;
        }
        return id;
      }
      return id;
    });
  }

  private formatVersionDate(dateStr: string): string {
    if (!dateStr) return '';
    return this.customDateFormatService.formatDate(dateStr, DateFormat.shortDate);
  }
}
